export const NAVBAR_SECTION_TITLES = Object.freeze({
  HOME: 'Home',
  COMPLIANCE: 'Compliance',
  AUDIT: 'Audit',
  ANALYZE: 'Analyze',
  SETTINGS: 'Settings',
  RISK_MANAGEMENT: 'Risk Management',
  INTEGRATIONS: 'Integrations',
  CUSTOMER_TRUST: 'Customer Trust',

  ANALYTICS: 'Analytics',
  ORGANIZATION: 'Organization',
  ADMINISTRATION: 'Administration',
})

export type NavbarSectionTitle =
  | (typeof NAVBAR_SECTION_TITLES)[keyof typeof NAVBAR_SECTION_TITLES]
  | 'Trustero Intelligence'

export const NAV_ITEM_TITLES = Object.freeze({
  DASHBOARD: 'Dashboard',
  HELP: 'Help',
  SERVICES: 'Services',
  RECEPTORS: 'Receptors',
  COMPANY_INFO: 'Company Info',
  POLICIES: 'Policies',
  CONTROLS: 'Controls',
  FRAMEWORKS: 'Frameworks',
  RISKS: 'Risk Register',
  AUDITS: 'Audits',
  REQUESTS: 'Requests',
  REPORT_SCAN: 'SOC 2 Report Summary',
  USERS: 'Users',
  ACTIVITY_LOG: 'Activity Log',
  ACTIVITIES: 'Activities',
  ACCOUNT_SWITCHER: 'Switch Account',
  AUDIT_BOT: 'Control Test',
  ROADMAP: 'Roadmap',
  AI_GRC_QA: 'Customer Questionnaires',
  VENDORS: 'Vendor Management',
  SCOPE: 'Scope',
  ACCOUNT_SETTINGS: 'Account Settings',
  YOUR_PREFERENCE: 'Your Preferences',
  MANAGE_DOCUMENTS: 'Manage GRC Documents',
  EVIDENCE: 'Evidence',
  GRC_PLATFORMS: 'GRC Platforms',
  DOCUMENT_MANAGEMENT: 'Document Management',
  USAGE: 'AI Usage',
  TRUSTERO_INTELLIGENCE: 'Trustero Intelligence (TI)',
  TRUST_PORTAL: 'Trust Portal',
  INDEXING: 'Indexing Status',
  WORKFLOW_MANAGEMENT: 'Workflow Management',
  GRC_DASHBOARDS: 'Dashboards',
  REPORTS: 'Reports',
  ACCESS_MANAGEMENT: 'Access Management',
  CONTENT_MANAGEMENT: 'Content Management',
  INTEGRATIONS_LIST: 'Integrations List',
  SCHEDULING: 'Scheduling',
  PROMPT_OVERRIDES: 'Prompt Overrides',
  INTERNAL_ACCOUNT_MANAGEMENT: 'Internal Admin Panel',
  RELEASE_NOTE_EDITOR: 'Release Note Editor',
  API_TOKENS: 'API Tokens',
})

export enum NAV_ITEM_KEYS {
  DASHBOARD = 'dashboard',
  HELP = 'help',
  SERVICES = 'services',
  RECEPTORS = 'receptors',
  COMPANY_INFO = 'company_info',
  POLICIES = 'policies',
  CONTROLS = 'controls',
  FRAMEWORKS = 'frameworks',
  RISKS = 'risks',
  AUDITS = 'audits',
  REQUESTS = 'requests',
  AI_SOC2_REPORT_SUMMARY = 'ai_soc2_report_summary',
  RISK_AI_SOC2_REPORT_SUMMARY = 'risk_soc2_report_summary',
  USERS = 'users',
  ACTIVITIES = 'activities',
  ACCOUNTS = 'accounts',
  AI_EXAMINATION_AND_TEST = 'ai_examination_and_test',
  COMPLIANCE_AI_EXAMINATION_AND_TEST = 'compliance_examination_and_test',
  ROADMAP = 'roadmap',
  AI_GRC_QA = 'ai_grc_qa',
  TRUSTERO_INTELLIGENCE = 'trustero_intelligence',
  VENDORS = 'vendors',
  SCOPE = 'scope',
  ACCOUNT_SETTINGS = 'account_settings',
  YOUR_PREFERENCE = 'your_preference',
  EVIDENCE = 'evidence',
  DOCUMENT_MANAGEMENT = 'document_management',
  GRC_PLATFORMS = 'grc_platforms',
  USAGE = 'usage',
  MANAGE_DOCUMENTS = 'manage_documents',
  TRUST_PORTAL_CONFIGURATION = 'trust_portal',
  INDEXING = 'indexing',
  WORKFLOW_MANAGEMENT = 'workflow_management',
  GRC_DASHBOARDS = 'grc_dashboards',
  REPORTS = 'reports',
  ACCESS_MANAGEMENT = 'access_management',
  INTEGRATIONS_LIST = 'integrations_list',
  SCHEDULING = 'scheduling',
  PROMPT_OVERRIDES = 'prompt_overrides',
  INTERNAL_ACCOUNT_MANAGEMENT = 'internal_admin_panel',
  RELEASE_NOTE_EDITOR = 'release_note_editor',
  API_TOKENS = 'api_tokens',
}

export type NavbarState = {
  [value in NAV_ITEM_KEYS]: boolean
}

// Be sure to also update app/src/components/Reusable/Accordions/NavBar/NavBarAccordions.constants.tsx
export const NavbarInitialState: NavbarState = {
  [NAV_ITEM_KEYS.DASHBOARD]: false,
  [NAV_ITEM_KEYS.HELP]: false,
  [NAV_ITEM_KEYS.SERVICES]: false,
  [NAV_ITEM_KEYS.RECEPTORS]: false,
  [NAV_ITEM_KEYS.COMPANY_INFO]: false,
  [NAV_ITEM_KEYS.POLICIES]: false,
  [NAV_ITEM_KEYS.CONTROLS]: false,
  [NAV_ITEM_KEYS.FRAMEWORKS]: false,
  [NAV_ITEM_KEYS.RISKS]: false,
  [NAV_ITEM_KEYS.AUDITS]: false,
  [NAV_ITEM_KEYS.REQUESTS]: false,
  [NAV_ITEM_KEYS.USERS]: false,
  [NAV_ITEM_KEYS.ACTIVITIES]: false,
  [NAV_ITEM_KEYS.ACCOUNTS]: false,
  [NAV_ITEM_KEYS.ROADMAP]: false,
  [NAV_ITEM_KEYS.AI_GRC_QA]: false,
  [NAV_ITEM_KEYS.VENDORS]: false,
  [NAV_ITEM_KEYS.SCOPE]: false,
  [NAV_ITEM_KEYS.ACCOUNT_SETTINGS]: false,
  [NAV_ITEM_KEYS.YOUR_PREFERENCE]: false,
  [NAV_ITEM_KEYS.EVIDENCE]: false,
  [NAV_ITEM_KEYS.DOCUMENT_MANAGEMENT]: false,
  [NAV_ITEM_KEYS.GRC_PLATFORMS]: false,
  [NAV_ITEM_KEYS.USAGE]: false,
  [NAV_ITEM_KEYS.MANAGE_DOCUMENTS]: false,
  [NAV_ITEM_KEYS.AI_EXAMINATION_AND_TEST]: false,
  [NAV_ITEM_KEYS.AI_SOC2_REPORT_SUMMARY]: false,
  [NAV_ITEM_KEYS.TRUSTERO_INTELLIGENCE]: false,
  [NAV_ITEM_KEYS.RISK_AI_SOC2_REPORT_SUMMARY]: false,
  [NAV_ITEM_KEYS.COMPLIANCE_AI_EXAMINATION_AND_TEST]: false,
  [NAV_ITEM_KEYS.TRUST_PORTAL_CONFIGURATION]: false,
  [NAV_ITEM_KEYS.INDEXING]: false,
  [NAV_ITEM_KEYS.WORKFLOW_MANAGEMENT]: false,
  [NAV_ITEM_KEYS.GRC_DASHBOARDS]: false,
  [NAV_ITEM_KEYS.REPORTS]: false,
  [NAV_ITEM_KEYS.ACCESS_MANAGEMENT]: false,
  [NAV_ITEM_KEYS.INTEGRATIONS_LIST]: false,
  [NAV_ITEM_KEYS.SCHEDULING]: false,
  [NAV_ITEM_KEYS.PROMPT_OVERRIDES]: false,
  [NAV_ITEM_KEYS.INTERNAL_ACCOUNT_MANAGEMENT]: false,
  [NAV_ITEM_KEYS.RELEASE_NOTE_EDITOR]: false,
  [NAV_ITEM_KEYS.API_TOKENS]: false,
}

export const AUDIT_ICON_COLOR = 'white'
export const BREAK_HEIGHT = 900
export const LOGO_HEIGHT = 40
